import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CircularProgress, Tooltip } from '@mui/material'
import {
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  DarkMode as DarkModeIcon,
  Email as EmailIcon,
  ErrorOutline as WarnIcon,
  LightMode as LightModeIcon,
  LockOpen as UnblockIcon,
  NotificationAdd as NotifyIcon,
  OpenInNew as OpenIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  QrCode2 as QrIcon,
  RemoveCircleOutline as UnknownIcon,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../store'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { NotifyDialogV2 } from '../component/subscriber-v2/NotifyDialogV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { Subscriber, SubscriberStatus } from '../store/user/reducer'
import { getSingleSubscriber, toggleBlock } from '../store/user/actionCreator'
import { getSubscriber } from '../store/common/actionCreator'
import { postBlackListIpAddress } from '../store/blacklist/actionCreator'
import { EnumSubscriberStatus } from '../enum/statusSubscriber'
import { formatDate } from '../function/date'
import { useAdminTheme } from '../hook/useAdminTheme'
import { PackagesCardV2 } from '../component/subscriber-v2/PackagesCardV2'
import { UsageCardV2 } from '../component/subscriber-v2/UsageCardV2'
import { SubscriberTicketsV2 } from '../component/subscriber-v2/SubscriberTicketsV2'
import { SubscriberRequestsV2 } from '../component/subscriber-v2/SubscriberRequestsV2'
import { ActivityCalendarV2 } from '../component/dashboard-v2/ActivityCalendarV2'
import { InvoicesV2 } from '../component/subscriber-v2/InvoicesV2'
import { AIAssistantV2 } from '../component/subscriber-v2/AIAssistantV2'
import { AdminActionsV2 } from '../component/subscriber-v2/AdminActionsV2'
import l from './SubscribersPageV2.module.css'
import s from './SubscriberPageV2.module.css'

type Tab = 'overview' | 'searches' | 'tickets' | 'invoices' | 'activity' | 'ai' | 'log'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'searches', label: 'Searches' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'activity', label: 'Activity calendar' },
  { key: 'ai', label: 'AI assistant' },
  { key: 'log', label: 'Admin actions' },
]

const STATUS_META: Record<string, { label: string; color: string }> = {
  [EnumSubscriberStatus.PAID]: { label: 'Paid', color: 'var(--ok)' },
  [EnumSubscriberStatus.TRIAL]: { label: 'Trial', color: 'var(--primary)' },
  [EnumSubscriberStatus.BLOCKED]: { label: 'Blocked', color: 'var(--danger)' },
  [EnumSubscriberStatus.DELETED]: { label: 'Deleted', color: 'var(--text3)' },
}

const PARTNERS_PER_PAGE = 5

function dateOrDash(value: string | Date | undefined | null, fmt: string) {
  if (!value) return '—'
  const formatted = formatDate(value, fmt)
  return formatted === 'undefined' || formatted === 'Invalid Date' ? '—' : formatted
}

function orDash(value: string | number | null | undefined) {
  return value === null || value === undefined || value === '' ? '—' : String(value)
}

type CheckState = 'ok' | 'bad' | 'unknown'

function Check({ state, label, value, children }: { state: CheckState; label: string; value?: React.ReactNode; children?: React.ReactNode }) {
  const icClass = state === 'ok' ? s.icOk : state === 'bad' ? s.icBad : s.icNeutral
  return (
    <div className={s.check}>
      <span className={`${s.ic} ${icClass}`}>
        {state === 'ok' ? <CheckIcon sx={{ fontSize: 15 }} /> : state === 'bad' ? <WarnIcon sx={{ fontSize: 15 }} /> : <UnknownIcon sx={{ fontSize: 15 }} />}
      </span>
      <span className={s.checkT}>{label}</span>
      <span className={s.checkV}>
        {value}
        {children}
      </span>
    </div>
  )
}

export function SubscriberPageV2() {
  const { id } = useParams() as { id: string }
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()
  // Filled by getSubscriber (the same request the old page makes); holds the
  // web/API usage counters.
  const usage = useSelector((state) => state.common.subscriber)

  const [subscriber, setSubscriber] = React.useState<Subscriber | null>(null)
  const [initialStatus, setInitialStatus] = React.useState<SubscriberStatus>()
  const [loading, setLoading] = React.useState(false)
  // The active tab lives in ?tab= so links can open a specific one (the
  // result page's "Back to subscriber" returns to ?tab=searches) and a reload
  // keeps it.
  const tabParam = searchParams.get('tab')
  const tab: Tab = TABS.some((t) => t.key === tabParam) ? (tabParam as Tab) : 'overview'
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'overview') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }
  const [copied, setCopied] = React.useState<string | null>(null)
  const [partnersPage, setPartnersPage] = React.useState(0)

  const [notifyOpen, setNotifyOpen] = React.useState(false)
  const [emailOpen, setEmailOpen] = React.useState(false)
  const [blockAsk, setBlockAsk] = React.useState(false)
  // "Blacklist IP" blacklists the IP and blocks the user — asked first
  const [blacklistAsk, setBlacklistAsk] = React.useState(false)

  React.useEffect(() => {
    setLoading(true)
    dispatch(
      getSingleSubscriber(Number(id), (data: Subscriber) => {
        setSubscriber(data)
        setInitialStatus(data.status)
        setLoading(false)
      }),
    )
  }, [id, dispatch])

  React.useEffect(() => {
    dispatch(getSubscriber(Number(id), 0, 100, 'all', 'year'))
  }, [id, dispatch])

  const copy = (value: string) => {
    navigator.clipboard?.writeText(value)
    setCopied(value)
    setTimeout(() => setCopied((current) => (current === value ? null : current)), 900)
  }

  const CopyBtn = ({ value }: { value?: string | null }) =>
    value ? (
      <Tooltip title="Copy">
        <button className={`${s.copyBtn} ${copied === value ? s.copyDone : ''}`} onClick={() => copy(value)} aria-label="Copy">
          {copied === value ? <CheckIcon sx={{ fontSize: 13 }} /> : <CopyIcon sx={{ fontSize: 13 }} />}
        </button>
      </Tooltip>
    ) : null

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}>
        <small>
          <span className={s.crumbLink} onClick={() => navigate('/users/subscribers_v2')}>Users / Subscribers</span>
        </small>
        <b>{subscriber ? [subscriber.firstname, subscriber.lastname].filter(Boolean).join(' ') || `Subscriber #${subscriber.id}` : 'Subscriber'}</b>
      </div>
      <div className={l.spacer} />
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  if (!subscriber || !subscriber.id) {
    return (
      <PrivatePage header={header}>
        <div className={`${l.theme} ${l.page}`} data-theme={theme}>
          <div className={s.center}>{loading ? <CircularProgress /> : <span className={l.muted}>Subscriber not found</span>}</div>
        </div>
      </PrivatePage>
    )
  }

  const name = [subscriber.firstname, subscriber.lastname].filter(Boolean).join(' ') || '—'
  const initials = name === '—' ? (subscriber.email || '?').slice(0, 1).toUpperCase() : name.split(/[\s-]+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase()
  const status = STATUS_META[subscriber.status] || { label: subscriber.status, color: 'var(--text3)' }
  const info = subscriber.registerInfo
  const ua = subscriber.userAgent
  const partners: any[] = Array.isArray(subscriber.partners) ? subscriber.partners : []
  const partnersPageCount = Math.max(1, Math.ceil(partners.length / PARTNERS_PER_PAGE))
  const visiblePartners = partners.slice(partnersPage * PARTNERS_PER_PAGE, (partnersPage + 1) * PARTNERS_PER_PAGE)
  const credits = subscriber.package?.creditsPerUnit ? subscriber.package.creditsPerUnit * Number(subscriber.packageBalance || 0) : null

  const webCount = typeof usage?.webUsagesCount === 'number' ? usage.webUsagesCount : null
  const apiCount = typeof usage?.apiUsagesCount === 'number' ? usage.apiUsagesCount : null

  // email_quality_score comes as a string like "0.86"
  const qualityRaw = info?.email_quality_score !== undefined && info?.email_quality_score !== null ? parseFloat(info.email_quality_score) : NaN
  const quality = Number.isNaN(qualityRaw) ? null : Math.round(qualityRaw <= 1 ? qualityRaw * 100 : qualityRaw)
  const qualityColor = quality === null ? 'var(--text3)' : quality >= 70 ? 'var(--ok)' : quality >= 40 ? 'var(--warn)' : 'var(--danger)'
  const looksReal =
    !!info &&
    info.email_quality_score !== '0.00' &&
    info.email_deliverability !== 'UNDELIVERABLE' &&
    info.email_is_disposable_email === false &&
    info.email_is_smtp_valid === true &&
    info.phone_valid === true &&
    ua?.isIpPhoneMatches === true

  const yesNo = (value: boolean | null | undefined, good: boolean): CheckState =>
    value === null || value === undefined ? 'unknown' : value === good ? 'ok' : 'bad'

  const isQr = !!ua?.utmSource && String(ua.utmSource).toLowerCase() === 'qr'
  const sourcePill = !ua ? null : isQr ? (
    <span className={`${l.pill} ${l.pillQr}`} style={{ maxWidth: 'none' }}>
      <QrIcon sx={{ fontSize: 14 }} />
      {ua.utmCampaign ? `${ua.utmCampaign} / QR-code` : 'QR-code'}
    </span>
  ) : ua.utmSource && ua.utmSource !== 'Unknown' ? (
    <span className={l.pill} style={{ maxWidth: 'none' }}>
      <span className={l.dot} style={{ background: 'var(--ok)' }} />
      {ua.utmSource}
    </span>
  ) : ua.utmSource === 'Unknown' ? (
    <span className={l.pill}>
      <span className={l.dot} style={{ background: 'var(--warn)' }} />
      Direct
    </span>
  ) : null

  const renderTab = () => {
    switch (tab) {
      case 'searches':
        return <div className={l.card}><SubscriberRequestsV2 data={subscriber} /></div>
      case 'tickets':
        return <SubscriberTicketsV2 data={subscriber} />
      case 'invoices':
        return <InvoicesV2 subscriber={subscriber} />
      case 'activity':
        return <div className={l.card}><ActivityCalendarV2 subscriber={subscriber} /></div>
      case 'ai':
        return <AIAssistantV2 subscriber={subscriber} dark={theme === 'dark'} />
      case 'log':
        return <AdminActionsV2 subscriber={subscriber} />
      default:
        return (
          <div className={s.grid}>
            <div className={s.stack}>
              <div className={l.card}>
                <div className={s.cardH}><h3>Packages</h3></div>
                <PackagesCardV2
                  data={subscriber}
                  onChange={(patch) => setSubscriber((current) => (current ? { ...current, ...patch } : current))}
                />
              </div>
              <div className={l.card}>
                <UsageCardV2 data={subscriber} />
              </div>
            </div>

            <div className={s.stack}>
              <div className={l.card}>
                <div className={s.cardH}><h3>Risk &amp; verification</h3></div>
                <div className={s.cardB}>
                  <div className={s.score}>
                    <div className={s.ring} style={{ background: `conic-gradient(${qualityColor} ${quality ?? 0}%, var(--surface3) 0)` }}>
                      <span>{quality ?? '—'}</span>
                    </div>
                    <div>
                      <b>{looksReal ? 'Looks like a real user' : info ? 'Needs a closer look' : 'No verification data'}</b>
                      <span className={l.sub}>E-mail quality score {orDash(info?.email_quality_score)}</span>
                    </div>
                  </div>
                  <Check
                    state={!info?.email_deliverability ? 'unknown' : info.email_deliverability === 'UNDELIVERABLE' ? 'bad' : 'ok'}
                    label="E-mail deliverable"
                    value={orDash(info?.email_deliverability)}
                  />
                  <Check state={yesNo(info?.email_is_disposable_email, false)} label="Not a disposable e-mail" value={info?.email_is_disposable_email ? 'disposable' : ''} />
                  <Check state={yesNo(info?.email_is_smtp_valid, true)} label="SMTP valid" />
                  <Check state={yesNo(ua?.isEmailDomainValid, true)} label="E-mail domain valid" />
                  <Check state={yesNo(info?.phone_valid, true)} label="Phone valid" value={orDash(info?.phone_type)} />
                  <Check state={yesNo(ua?.isIpPhoneMatches, true)} label="Phone country matches IP" />
                  <Check state={!ua?.isProxy ? 'unknown' : ua.isProxy === 'yes' ? 'bad' : 'ok'} label="No proxy" value={orDash(ua?.isProxy)} />
                  <Check
                    state={typeof ua?.ipMatchesCount !== 'number' ? 'unknown' : ua.ipMatchesCount > 0 ? 'bad' : 'ok'}
                    label="IP used by other accounts"
                    value={typeof ua?.ipMatchesCount === 'number' ? `${ua.ipMatchesCount} matches` : '—'}
                  >
                    {typeof ua?.ipMatchesCount === 'number' && ua.ipMatchesCount > 0 && ua.handlIP && !subscriber.isDeleted && (
                      <>
                        {' · '}
                        <button
                          className={s.link}
                          style={{ color: 'var(--danger)' }}
                          onClick={() => setBlacklistAsk(true)}
                        >
                          Blacklist IP
                        </button>
                      </>
                    )}
                  </Check>
                </div>
              </div>

              <div className={l.card}>
                <div className={s.cardH}><h3>Registration source</h3></div>
                <div className={s.cardB}>
                  {sourcePill && <div style={{ marginBottom: 12 }}>{sourcePill}</div>}
                  <dl className={s.kv}>
                    <dt>UTM source</dt><dd>{orDash(ua?.utmSource)}</dd>
                    <dt>UTM campaign</dt><dd>{orDash(ua?.utmCampaign)}</dd>
                    <dt>UTM medium</dt><dd>{orDash(ua?.utmMedium)}</dd>
                    <dt>UTM term</dt><dd>{orDash(ua?.utmTerm)}</dd>
                    <dt>UTM content</dt><dd>{orDash(ua?.utmContent)}</dd>
                    <dt>GA client ID</dt><dd className={l.mono}>{orDash(ua?.gaClientId)}</dd>
                    <dt>handl custom 1</dt><dd>{orDash(ua?.handlCustom1)}</dd>
                    <dt>IP (cookie)</dt><dd className={l.mono}>{orDash(ua?.handlIP)}</dd>
                    <dt>Registration IP</dt><dd className={l.mono}>{orDash(subscriber.registrationIp)}</dd>
                  </dl>
                </div>
              </div>

              {partners.length > 0 && (
                <div className={l.card}>
                  <div className={s.cardH}><h3>Partners</h3><span className={s.hint}>· {partners.length}</span></div>
                  <div className={s.cardB}>
                    {visiblePartners.map((partner) => (
                      <div key={partner.id} className={s.partner}>
                        <b className={l.mono}>#{partner.id}</b>
                        {/* who the partner is, then their API key under it */}
                        <span className={s.partnerWho}>
                          <span className={s.partnerName}>
                            {[partner.firstname, partner.lastname].filter(Boolean).join(' ') || <span className={l.muted}>No name</span>}
                          </span>
                          <Tooltip title={partner.apiKey?.createdTime ? `Created ${new Date(partner.apiKey.createdTime).toLocaleString()}` : ''}>
                            <span className={`${l.mono} ${l.muted} ${s.partnerKey}`}>{partner.apiKey?.key || 'No API key'}</span>
                          </Tooltip>
                        </span>
                        <span className={l.status}>
                          <span className={l.dot} style={{ background: partner.isDeleted ? 'var(--danger)' : 'var(--ok)' }} />
                          {partner.isDeleted ? 'Deleted' : 'Active'}
                        </span>
                        <Tooltip title="Open partner">
                          <button className={s.copyBtn} onClick={() => window.open(`/users/subscriber_v2/${partner.id}`, '_blank', 'noopener,noreferrer')}>
                            <OpenIcon sx={{ fontSize: 15 }} />
                          </button>
                        </Tooltip>
                      </div>
                    ))}
                    {partnersPageCount > 1 && (
                      <div className={l.pager} style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                        <button className={l.pg} disabled={partnersPage === 0} onClick={() => setPartnersPage(partnersPage - 1)}>‹</button>
                        <span className={l.muted} style={{ alignSelf: 'center' }}>{partnersPage + 1} / {partnersPageCount}</span>
                        <button className={l.pg} disabled={partnersPage >= partnersPageCount - 1} onClick={() => setPartnersPage(partnersPage + 1)}>›</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
    }
  }

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.card} ${s.hero}`}>
          {subscriber.avatar ? <img className={s.heroAva} src={subscriber.avatar} alt="" /> : <div className={s.heroAva}>{initials}</div>}
          <div className={s.heroMain}>
            <h1 className={s.heroName}>
              {name}
              <span className={s.statusPill} style={{ color: status.color, background: `color-mix(in srgb, ${status.color} 12%, transparent)` }}>
                <i />
                {status.label}
              </span>
            </h1>
            <div className={s.meta}>
              <span>ID <b className={l.mono}>{subscriber.id}</b></span>
              <span>UID <span className={l.mono}>{subscriber.uid}</span><CopyBtn value={subscriber.uid} /></span>
              <span><CalendarIcon sx={{ fontSize: 14 }} />Subscriber since {dateOrDash(subscriber.createdTime, 'dd/MM/yyyy')}</span>
            </div>
            <div className={s.meta}>
              {subscriber.email && <span><EmailIcon sx={{ fontSize: 14 }} />{subscriber.email}<CopyBtn value={subscriber.email} /></span>}
              {subscriber.phone && <span><PhoneIcon sx={{ fontSize: 14 }} />{subscriber.phone}<CopyBtn value={subscriber.phone} /></span>}
              {subscriber.country && <span><PlaceIcon sx={{ fontSize: 14 }} />{subscriber.country}</span>}
            </div>
          </div>
          <div className={s.actions}>
            <button className={s.btn} onClick={() => setNotifyOpen(true)} disabled={subscriber.isDeleted}>
              <NotifyIcon sx={{ fontSize: 16 }} />
              Notify
            </button>
            <button className={s.btn} onClick={() => setEmailOpen(true)} disabled={subscriber.isDeleted}>
              <EmailIcon sx={{ fontSize: 16 }} />
              E-mail
            </button>
            {subscriber.isDeleted ? (
              <span className={s.deletedTag}>Deleted</span>
            ) : subscriber.isBlocked ? (
              <button
                className={`${s.btn} ${s.btnOk}`}
                onClick={() =>
                  dispatch(
                    toggleBlock(subscriber.id, false, () =>
                      setSubscriber((current) =>
                        current ? { ...current, isBlocked: false, status: initialStatus && initialStatus !== EnumSubscriberStatus.BLOCKED ? initialStatus : EnumSubscriberStatus.TRIAL } : current,
                      ),
                    ),
                  )
                }
              >
                <UnblockIcon sx={{ fontSize: 16 }} />
                Unblock
              </button>
            ) : (
              <button className={`${s.btn} ${s.btnDanger}`} onClick={() => setBlockAsk(true)}>
                <BlockIcon sx={{ fontSize: 16 }} />
                Block
              </button>
            )}
          </div>
        </div>

        <div className={s.kpis}>
          <div className={l.kpi}>
            <small>Balance</small>
            <b>{orDash(subscriber.packageBalance)}€</b>
            <span>{credits !== null ? `${credits.toLocaleString()} credits` : subscriber.package?.title || 'No package'}</span>
          </div>
          <div className={l.kpi}>
            <small>Package renewal</small>
            <b>{dateOrDash(subscriber.packageEndDate, 'dd/MM/yyyy')}</b>
            <span className={s.tog}><span className={l.dot} style={{ background: subscriber.isAutoRenewal ? 'var(--ok)' : 'var(--text3)' }} />Auto-renewal {subscriber.isAutoRenewal ? 'on' : 'off'}</span>
            <span className={s.tog}><span className={l.dot} style={{ background: subscriber.isAutoRefill ? 'var(--ok)' : 'var(--text3)' }} />Auto-refill {subscriber.isAutoRefill ? 'on' : 'off'}</span>
          </div>
          <div className={l.kpi}>
            <small>Package</small>
            <b>{subscriber.package?.title || '—'}</b>
            <span>
              {[subscriber.package?.isAiAssistant ? 'AI assistant' : null, subscriber.package?.isIrbisPro ? 'IRBIS Pro' : null, subscriber.additionalPackage?.title ? `+ ${subscriber.additionalPackage.title}` : null]
                .filter(Boolean)
                .join(' · ') || `LTV ${orDash(subscriber.ltv)}`}
            </span>
          </div>
          <div className={l.kpi}>
            <small>Searches</small>
            <b>{webCount !== null || apiCount !== null ? ((webCount || 0) + (apiCount || 0)).toLocaleString() : '…'}</b>
            <span>{orDash(webCount)} web · {orDash(apiCount)} API</span>
          </div>
        </div>

        <div className={s.tabs}>
          {TABS.map((t) => (
            <button key={t.key} className={`${s.tab} ${tab === t.key ? s.tabOn : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
              {t.key === 'searches' && webCount !== null && <span className={s.tabCount}>{((webCount || 0) + (apiCount || 0)).toLocaleString()}</span>}
            </button>
          ))}
        </div>

        {renderTab()}
      </div>

      {notifyOpen && <NotifyDialogV2 mode="notification" subscribers={[subscriber]} onClose={() => setNotifyOpen(false)} />}
      {emailOpen && <NotifyDialogV2 mode="email" subscribers={[subscriber]} onClose={() => setEmailOpen(false)} />}
      {blacklistAsk && ua?.handlIP && (
        <ConfirmV2
          header={`Blacklist ${ua.handlIP} and block ${subscriber.email || 'this user'}?`}
          yesLabel="Blacklist & block"
          danger
          onYes={() => dispatch(postBlackListIpAddress({ ipAddress: ua.handlIP, blockUser: true }))}
          onClose={() => setBlacklistAsk(false)}
        />
      )}
      {blockAsk && (
        <ConfirmV2
          header={`Block ${subscriber.email || 'this user'}? They will not be able to sign in.`}
          onYes={() =>
            dispatch(
              toggleBlock(subscriber.id, true, () =>
                setSubscriber((current) => (current ? { ...current, isBlocked: true, status: EnumSubscriberStatus.BLOCKED } : current)),
              ),
            )
          }
          onClose={() => setBlockAsk(false)}
        />
      )}
    </PrivatePage>
  )
}
